import { FilterValue } from 'src/modules/shared/types/filters.types';

/**
 * Converts a date string to ISO format accounting for user's timezone
 *
 * @param value - The date value to convert
 * @returns Converted ISO string or original value if conversion fails
 */
export const convertDateValueToISO = (value: FilterValue): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    // Handle Date objects
    if (value instanceof Date) {
      return value.toISOString();
    }

    // Handle string values
    if (typeof value === 'string') {
      // Handle date-only format: "YYYY-MM-DD"
      if (isDateOnlyFormat(value)) {
        const [year, month, day] = value.split('-').map(Number);
        const localMidnight = new Date(year, month - 1, day, 0, 0, 0, 0);

        if (!isNaN(localMidnight.getTime())) {
          return localMidnight.toISOString();
        }
      }

      // Handle datetime-local format: "YYYY-MM-DDTHH:MM"
      if (isDateTimeFormat(value)) {
        const date = new Date(value);

        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }

      // Already an ISO string or other format - return as-is
      return value;
    }

    return null;
  } catch (error) {
    console.error('Error converting date value to ISO:', value, error);
    return null;
  }
};

/**
 * Converts a date value to the end of the day in ISO format
 *
 * @param value - The date value to convert
 * @returns ISO string representing end of the day (start of next day)
 */
export const convertDateValueToEndOfDay = (
  value: FilterValue
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  try {
    let date: Date | null = null;

    // Handle Date objects
    if (value instanceof Date) {
      date = value;
    }

    // Handle string values
    if (typeof value === 'string') {
      // Handle date-only format: "YYYY-MM-DD"
      if (isDateOnlyFormat(value)) {
        const [year, month, day] = value.split('-').map(Number);
        date = new Date(year, month - 1, day, 0, 0, 0, 0);
      }

      // Handle datetime-local format: "YYYY-MM-DDTHH:MM"
      if (isDateTimeFormat(value)) {
        date = new Date(value);
      }
    }

    if (!date || isNaN(date.getTime())) {
      return null;
    }

    // Add one day to get the start of the next day
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);

    return nextDay.toISOString();
  } catch (error) {
    console.error('Error converting date to end of day:', value, error);
    return null;
  }
};

/**
 * Checks if a value represents a datetime format (YYYY-MM-DDTHH:MM)
 */
export const isDateTimeFormat = (value: string | FilterValue): boolean => {
  if (typeof value === 'string') {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value);
  }

  return false;
};

/**
 * Checks if a value represents a date-only format (YYYY-MM-DD)
 */
export const isDateOnlyFormat = (value: string | FilterValue): boolean => {
  if (typeof value === 'string') {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  return false;
};
